import {useRouter} from '@/hooks/useAppRouter';
import ReactMarkdown, {type Components} from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {SearchResultsGrid} from '../search-results-grid';
import type {ChatMessage} from '../types';
import styles from '../style.module.css';
import {formatDateShort} from '@/util/date-format';

interface MessageBubbleProps {
    message: ChatMessage;
}

function formatTimestamp(timestamp: Date): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return formatDateShort(date);
}

interface MarkdownLinkProps {
    href?: string;
    children?: React.ReactNode;
    [key: string]: unknown;
}

function MarkdownLink({href, children, ...props}: MarkdownLinkProps) {
    const router = useRouter();
    const isInternal = href?.startsWith('/');

    if (isInternal && href) {
        return (
            <a
                {...props}
                href={href}
                onClick={(e) => {
                    e.preventDefault();
                    router.push(href);
                }}
                style={{
                    color: '#333',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                }}
            >
                {children}
            </a>
        );
    }

    return (
        <a
            {...props}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                color: '#333',
                textDecoration: 'underline',
            }}
        >
            {children}
        </a>
    );
}

interface AssistantMessageContentProps {
    content: string;
    searchResults?: ChatMessage['searchResults'];
}

function AssistantMessageContent({
    content,
    searchResults,
}: AssistantMessageContentProps) {
    const hasMarker = content.includes('---RESULTS---');
    const hasResults = !!searchResults;

    const markdownComponents: Components = {
        a: ({node: _node, ...props}) => <MarkdownLink {...props} />,
    };

    if (hasMarker) {
        const parts = content.split('---RESULTS---');
        const beforeResults = parts[0].trim();
        const afterResults = parts[1]?.trim() ?? '';

        return (
            <>
                {beforeResults && (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                    >
                        {beforeResults}
                    </ReactMarkdown>
                )}
                {hasResults && searchResults && (
                    <SearchResultsGrid
                        results={searchResults.results}
                        type={searchResults.type}
                    />
                )}
                {afterResults && (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                    >
                        {afterResults}
                    </ReactMarkdown>
                )}
            </>
        );
    }

    // No marker - render normally
    return (
        <>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
            >
                {content}
            </ReactMarkdown>
            {searchResults && (
                <SearchResultsGrid
                    results={searchResults.results}
                    type={searchResults.type}
                />
            )}
        </>
    );
}

export function MessageBubble({message}: MessageBubbleProps) {
    return (
        <div className={`${styles.message} ${styles[message.role]}`}>
            <div className={styles.messageBubble}>
                {message.role === 'assistant' ? (
                    <AssistantMessageContent
                        content={message.content}
                        searchResults={message.searchResults}
                    />
                ) : (
                    message.content
                )}
            </div>
            <div className={styles.messageTimestamp}>
                {formatTimestamp(message.timestamp)}
            </div>
        </div>
    );
}
